// Barra superior Consola V3 (Central Galarya). Reloj Tijuana, Live, KPIs multidimensionales y un
// ÚNICO control de sonido (bocina con menú). El cambio de tema vive SOLO en el encabezado global
// del shell: aquí no se duplica.
import { Volume2, VolumeX, Radio, WifiOff, FlaskConical, TriangleAlert, Check, Play } from "lucide-react";
import ScenarioSwitch from "@/components/ScenarioSwitch";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

function Kpi({ n, label, tone, dot, testId, title }) {
  // Contador en CERO: neutro. Con valor positivo recibe su color semántico.
  const cero = !n;
  return (
    <div data-testid={testId} title={title} className="flex flex-col items-start leading-none">
      <span className={`inline-flex items-center gap-1.5 text-2xl font-extrabold tabular-nums ${cero ? "gv2-zero-count" : tone}`}>
        <span className={`h-2.5 w-2.5 rounded-full ${cero ? "gv2-dot-zero" : dot}`} /> {n}
      </span>
      <span className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</span>
    </div>
  );
}

// Tres estados REALES del audio: 'on' (preferencia ON y contexto reproduciendo), 'muted'
// (silenciado por el operador) y 'blocked' (preferencia ON pero el navegador no permite reproducir).
const AUDIO_TEXTO = {
  on: { corto: "Sonido de alertas ON", titulo: "Sonido de alertas activado y reproducible" },
  muted: { corto: "Sonido silenciado", titulo: "Sonido silenciado por el operador" },
  blocked: { corto: "Sonido bloqueado", titulo: "El navegador no permite reproducir todavía: toca para activar" },
};

export default function TopBarV3({
  reloj, live, counts, audioState = "muted", onToggleAudio, onTestSound, demoMode = false,
}) {
  const audioOn = audioState !== "muted";
  const txt = AUDIO_TEXTO[audioState] || AUDIO_TEXTO.muted;
  return (
    <header className="shrink-0 border-b border-[#E2E8F0] bg-white">
      <div className="flex flex-nowrap items-center gap-x-2 px-3 py-1.5 sm:gap-x-4 sm:px-4 sm:py-2.5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          {/* VIS-01R2 · el logo Galarya vive UNA sola vez, en el header global del shell. Aquí
              identificamos el MÓDULO (Central) con su propio icono, nombre y reloj. */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="gv2-module-icon">
              <Radio className="h-5 w-5" strokeWidth={1.9} />
            </span>
            <div className="flex min-w-0 flex-col leading-tight">
              <span className="text-[13px] font-extrabold tracking-tight text-[#012472] sm:text-sm">CENTRAL GALARYA</span>
              <span data-testid="v3-reloj" className="whitespace-nowrap text-[11px] font-mono tabular-nums text-slate-500 sm:text-xs">{reloj}</span>
            </div>
          </div>
          <span data-testid="v3-live" title={demoMode ? "Escenario de demostración" : live ? "Canal en vivo" : "Sin señal en vivo"}
            className={`gv2-chip inline-flex shrink-0 items-center ${
              demoMode ? "gv2-chip-alert"
              : live ? "gv2-chip-live" : "gv2-chip-alert"}`}>
            {demoMode ? <FlaskConical className="h-3.5 w-3.5" /> : live ? <Radio className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            {demoMode ? "DEMO" : live ? "EN VIVO" : "SIN SEÑAL"}
          </span>
          <div className="hidden lg:block h-10 w-px bg-slate-200" />
          <div className="hidden lg:flex items-center gap-5">
            {/* UI-01D · la unidad de cada contador viaja en la etiqueta: son CASOS, no personas, y
                la cobertura crítica cuenta EQUIPOS. Los algoritmos de conteo no cambian. */}
            <Kpi n={counts.altaSinTomar} label="Casos ALTA sin tomar" title="Casos de persona con prioridad ALTA que nadie ha tomado todavía."
              tone="gv2-high-count" dot="gv2-dot-alert" testId="v3-kpi-alta" />
            <Kpi n={counts.mediaSinTomar} label="Casos MEDIA sin tomar" title="Casos de persona con prioridad MEDIA que nadie ha tomado todavía."
              tone="gv2-medium-count" dot="gv2-dot-amber" testId="v3-kpi-media" />
            <Kpi n={counts.enAtencion} label="Casos en atención" title="Casos ya tomados y en curso (persona y técnicos)."
              tone="gv2-action-count" dot="gv2-dot-action" testId="v3-kpi-atencion" />
            {/* El violeta identifica la NATURALEZA TÉCNICA; nunca sustituye a rojo/ámbar como prioridad. */}
            <Kpi n={counts.coberturaCritica} label="Cobertura crítica · equipos" title="Incidencias técnicas de cobertura crítica sin tomar. Cuenta EQUIPOS, no casos de persona."
              tone="gv2-tech-count" dot="gv2-dot-tech" testId="v3-kpi-cobertura" />
          </div>
        </div>

        <div className="ml-auto flex shrink-0 flex-nowrap items-center justify-end gap-1 sm:gap-2">
          <div className="gv2-scenario min-w-0 max-w-[9rem] sm:max-w-none"><ScenarioSwitch /></div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="v3-audio-menu" data-audio-estado={audioState}
                aria-label="Controles de sonido de alertas" title={txt.titulo}
                className={`gv2-audio-btn inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]`}>
                {audioState === "on" ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                {audioState === "blocked" && <TriangleAlert className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline">{txt.corto}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs font-semibold">{txt.titulo}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="v3-audio-toggle" onSelect={onToggleAudio} className="gap-2 text-sm">
                {audioOn ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                {audioOn ? "Sonido activado" : "Sonido silenciado"}
                {audioOn && <Check className="ml-auto h-4 w-4" />}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="v3-audio-test" onSelect={onTestSound} className="gap-2 text-sm">
                <Play className="h-4 w-4" /> Probar sonido
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
